import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const p10Faqs: LearnFAQ[] = [
  {
    question: "What is NeuroStar TMS therapy?",
    answer:
      "NeuroStar is a repetitive TMS system made by Neuronetics and was the first TMS device FDA-cleared for depression, in October 2008. It uses a figure-8 coil positioned over the left prefrontal cortex to deliver 10 Hz magnetic pulses in sessions of roughly 19 to 37 minutes, five days a week for about four to six weeks. It is cleared for adults with major depressive disorder who have not improved on antidepressant medication, for anxiety symptoms in adults with MDD, and, since 2024, for adolescents aged 15 to 21.",
  },
  {
    question: "What is the difference between BrainsWay and NeuroStar?",
    answer:
      "Both are FDA-cleared rTMS systems for depression. NeuroStar uses a focal figure-8 coil; BrainsWay uses an H-coil inside a helmet that stimulates a deeper and broader area. BrainsWay additionally holds clearances for OCD (2018), smoking cessation (2020), and anxious depression (2021); NeuroStar holds clearances for anxious depression (2022) and adolescents (2024). No head-to-head randomized trial has shown one to be more effective than the other for depression.",
  },
  {
    question: 'What is BrainsWay accelerated TMS or the "swift protocol"?',
    answer:
      "Some clinics deliver more than one BrainsWay Deep TMS session per day and describe this as accelerated or swift treatment. Accelerated schedules on conventional devices are off-label modifications of the cleared once-daily protocol, and their evidence base is smaller and more varied than for standard courses. The only accelerated TMS protocol with its own FDA clearance is SAINT on the Magnus Neuromodulation System. Ask any clinic offering accelerated TMS whether the schedule is cleared or off-label.",
  },
  {
    question: "What is Exomind therapy?",
    answer:
      "Exomind is a 510(k)-cleared TMS device made by BTL Industries, a company best known for aesthetic equipment, and has been marketed since 2024. Its clearance is for depression in adults. It is often offered in med spas and wellness clinics with claims about brain fog, focus, perimenopause, or binge eating; those uses are off-label, and a 2026 Clinical TMS Society journal statement found them unsupported by peer-reviewed evidence. If you are offered Exomind, ask whether a psychiatrist will evaluate you and whether the use is within the cleared indication.",
  },
  {
    question: "Is SAINT better than regular TMS?",
    answer:
      "Not established. SAINT produced a 78.6% remission rate versus 13.3% for sham at four weeks in a single-site randomized trial of 29 analyzed patients, which is a striking result but from a very small study with short follow-up. It has never been compared head-to-head with a standard six-week course. SAINT's advantages are its five-day schedule and MRI-based targeting; its limitations are limited durability data, the MRI requirement, and cost, since it is often self-pay.",
  },
  {
    question: "Is deep brain stimulation the same as TMS?",
    answer:
      "No. Deep brain stimulation is neurosurgery: electrodes are implanted deep in the brain and connected to a battery under the skin of the chest. It is approved for Parkinson's disease and essential tremor and remains investigational for depression, with mixed trial results. TMS is non-invasive, delivered through a coil on the scalp in an outpatient office, with no surgery or anesthesia. No office-based magnetic treatment is deep brain stimulation, even if marketed with that phrase.",
  },
  {
    question: "What is the best TMS machine?",
    answer:
      "There is no evidence-based answer, because no large randomized trial has compared manufacturers' devices against each other. What matters more is whether the device is FDA-cleared for your diagnosis, whether the protocol you will receive is supported by published trials, whether a psychiatrist supervises your care, how response will be measured, and what the plan is if you do not improve. A well-run clinic using any cleared device for its cleared indication is a better choice than a newer machine used off-label.",
  },
  {
    question: "What is Ampa TMS?",
    answer:
      "Ampa One is a newer TMS system that appears in patient searches. Reliable, verified details about its cleared indication, coil design, and protocol were not available for this article, so we do not describe them. Before considering treatment on any device, search the FDA's 510(k) database by device or manufacturer name to confirm what it is cleared for, and ask the clinic the same questions you would ask about any TMS system.",
  },
];

export const page10: LearnPageData = {
  slug: "tms-devices-compared-saint-neurostar-brainsway-exomind",
  title: "TMS Devices Compared: SAINT, NeuroStar, BrainsWay, Exomind",
  description:
    "NeuroStar, BrainsWay Deep TMS, Magnus SAINT, and Exomind compared by coil design, FDA-cleared indications, protocol, and targeting, plus why DBS is not TMS.",
  canonical: "/learn/tms-devices-compared-saint-neurostar-brainsway-exomind",
  h1: "How Do TMS Devices Differ? SAINT®, NeuroStar®, BrainsWay®, and Exomind™ Compared",
  breadcrumbLabel: "TMS Devices Compared",
  crumbs: baseCrumbs("TMS Devices Compared"),
  shortAnswer:
    "TMS devices differ in coil design, FDA-cleared indications, and protocol. NeuroStar (figure-8 coil) and BrainsWay (H-coil) are standard once-daily systems. SAINT® (Magnus) is the only FDA-cleared accelerated protocol. Exomind is a newer device often marketed off-label. Call Neuro Wellness TMS Centers of America at 954-827-9793.",
  keyTakeaways: [
    "NeuroStar (figure-8 coil, 2008) and BrainsWay (H-coil, deeper stimulation) are the two most common conventional TMS systems.",
    "BrainsWay H7 coil is FDA-cleared for OCD; NeuroStar is cleared for anxious depression and adolescents 15–21.",
    "SAINT® (Magnus, K220177) is the only accelerated TMS protocol with its own FDA clearance.",
    "Exomind (BTL) is cleared for depression but often marketed off-label for brain fog, focus, etc.",
    "No head-to-head trial shows one device is more effective than another; the clinic and protocol matter more than the brand.",
  ],
  sections: [
    {
      heading: "NeuroStar® Advanced Therapy",
      body: (
        <>
          {P(
            "NeuroStar, made by Neuronetics, was the first TMS device FDA-cleared for depression, in October 2008. It uses a figure-8 coil positioned over the left dorsolateral prefrontal cortex to deliver 10 Hz magnetic pulses in sessions of roughly 19 to 37 minutes, five days a week for about four to six weeks.",
          )}
          {P(
            "NeuroStar is cleared for adults with major depressive disorder who have not improved on antidepressant medication, for anxiety symptoms in adults with MDD (2022), and for adolescents aged 15 to 21 (2024). It is one of the most widely used TMS systems in the United States.",
          )}
        </>
      ),
    },
    {
      heading: "BrainsWay Deep TMS",
      body: (
        <>
          {P(
            "BrainsWay uses an H-coil inside a helmet that stimulates a deeper and broader area than a figure-8 coil. It is FDA-cleared for depression, anxious depression (2021), OCD (2018, H7 coil), and smoking cessation (2020).",
          )}
          {P(
            "No head-to-head randomized trial has shown BrainsWay to be more effective than NeuroStar for depression. The choice between them often comes down to the clinic's preference and the specific indication.",
          )}
        </>
      ),
    },
    {
      heading: "SAINT® (Magnus Neuromodulation System)",
      body: (
        <>
          {P(
            "SAINT® is the only accelerated TMS protocol with its own FDA clearance (510(k) K220177, September 2022). It delivers ten iTBS sessions per day for five consecutive days, with the stimulation target chosen from each patient's functional MRI. It is cleared for adults with major depressive disorder who did not improve enough on antidepressant medication.",
          )}
          {P(
            "SAINT® has never been compared head-to-head with a standard six-week course. Its advantages are the five-day schedule and MRI-based targeting; its limitations are limited durability data, the MRI requirement, and cost.",
          )}
        </>
      ),
    },
    {
      heading: "Exomind™ (BTL Industries)",
      body: (
        <>
          {P(
            "Exomind is a 510(k)-cleared TMS device made by BTL Industries, marketed since 2024. Its clearance is for depression in adults. It is often offered in med spas and wellness clinics with claims about brain fog, focus, perimenopause, or binge eating — those uses are off-label, and a 2026 Clinical TMS Society journal statement found them unsupported by peer-reviewed evidence.",
          )}
          {P(
            "If you are offered Exomind, ask whether a psychiatrist will evaluate you and whether the use is within the cleared indication.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status and how to choose",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> All of these devices are
            FDA-cleared (510(k)), not FDA-approved — that term applies to drugs.
            No large randomized trial has compared manufacturers' devices
            against each other. What matters more than the brand is whether the
            device is FDA-cleared for your diagnosis, whether the protocol is
            supported by published trials, whether a psychiatrist supervises
            your care, and what the plan is if you do not improve. Deep brain
            stimulation (DBS) is neurosurgery and is not TMS.
          </p>
        </div>
      ),
    },
  ],
  faqs: p10Faqs,
  sources: [
    {
      citation:
        "FDA 510(k) Premarket Notification K220177 — Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "Neuronetics, Inc. NeuroStar Advanced Therapy TMS System — FDA clearances and indications.",
      url: "https://neurostar.com/",
    },
    {
      citation:
        "BrainsWay Ltd. Deep TMS System — FDA clearances and indications.",
      url: "https://brainsway.com/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/tms-devices-compared-saint-neurostar-brainsway-exomind#webpage`,
        name: "How Do TMS Devices Differ? SAINT®, NeuroStar®, BrainsWay®, and Exomind™ Compared",
        url: `${SITE_URL}/learn/tms-devices-compared-saint-neurostar-brainsway-exomind`,
        description:
          "NeuroStar, BrainsWay Deep TMS, Magnus SAINT, and Exomind compared by coil design, FDA-cleared indications, protocol, and targeting, plus why DBS is not TMS.",
        about: [
          {
            "@type": "MedicalCondition",
            name: "Major depressive disorder",
            code: {
              "@type": "MedicalCode",
              code: "D003865",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Treatment-resistant depression",
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        name: "Repetitive transcranial magnetic stimulation (rTMS)",
        alternateName: "TMS therapy",
        procedureType: "Noninvasive",
        relevantSpecialty: { "@type": "MedicalSpecialty", name: "Psychiatry" },
      },
      {
        "@type": "MedicalTherapy",
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT)",
        alternateName: "Magnus Neuromodulation System with SAINT Technology",
        procedureType: "Noninvasive",
      },
      {
        "@type": "MedicalDevice",
        name: "NeuroStar Advanced Therapy TMS System",
        manufacturer: { "@type": "Organization", name: "Neuronetics, Inc." },
      },
      {
        "@type": "MedicalDevice",
        name: "BrainsWay Deep TMS",
        manufacturer: { "@type": "Organization", name: "BrainsWay Ltd." },
      },
      {
        "@type": "MedicalDevice",
        name: "Magnus Neuromodulation System with SAINT Technology",
        manufacturer: { "@type": "Organization", name: "Magnus Medical, Inc." },
      },
      {
        "@type": "MedicalDevice",
        name: "Exomind",
        manufacturer: { "@type": "Organization", name: "BTL Industries" },
      },
      faqJsonld(
        p10Faqs,
        `${SITE_URL}/learn/tms-devices-compared-saint-neurostar-brainsway-exomind#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("TMS Devices Compared")),
    ],
  },
  relatedLinks: sharedRelated,
};
